import Image from 'next/image'

import { stegaClean } from '@sanity/client/stega'
import { PortableTextBlock, toPlainText } from 'next-sanity'

import { Button, buttonSizeClasses } from '@/app/components/Button'
import { Icon } from '@/app/components/Icon'
import PortableText from '@/app/components/PortableText'
import { TrackingLocation } from '@/app/utils/analytics'
import { cn } from '@/app/utils/style'
import { Callout as CalloutRaw } from '@/sanity.types'
import { dataAttr, urlForImage } from '@/sanity/lib/utils'

export type CalloutProps = {
  callout: CalloutRaw & { _key?: string }
  tracking: TrackingLocation
  className?: string
  textClassName?: string
  documentId?: string
  documentType?: string
  parentPath?: string
}

const alignmentClasses = {
  left: 'items-start text-left justify-start',
  center: 'items-center text-center justify-center',
  right: 'items-end text-right justify-end',
}

const buttonAlignmentClasses = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
}

const bgColorClasses = {
  'neutral-50': 'bg-neutral-50',
  'neutral-100': 'bg-neutral-100',
  'blue-50': 'bg-blue-50',
}

const headingSizeClasses = {
  default: 'size-subheading-sm lg:size-subheading-lg',
  small: 'size-subheading-xs lg:size-subheading-sm',
}

export const Callout = ({
  callout,
  tracking,
  className,
  textClassName,
  documentId,
  documentType,
  parentPath,
}: CalloutProps) => {
  if (!callout) return null

  const alignment = stegaClean(callout.alignment) || 'left'
  const bgColor = stegaClean(callout.bgColor) || 'neutral-50'
  const headingSize = stegaClean(callout.headingSize) || 'default'
  const showBorder = stegaClean(callout.showBorder) ?? false

  const imageBaseUrl = urlForImage(callout.image)?.url()
  const isArticleCallout = callout.showImage && documentType === 'guide'

  const attr =
    callout._key && documentId && documentType && parentPath
      ? dataAttr({
          id: documentId,
          type: documentType,
          path: `${parentPath}[_key=="${callout._key}"]`,
        })
      : undefined

  return (
    <div
      className={cn(
        'flex flex-col gap-0 rounded-md overflow-hidden w-full',
        alignmentClasses[alignment],
        bgColorClasses[bgColor],
        showBorder && 'border-3 lg:border-4 border-primary-200',
        isArticleCallout && 'md:flex-row md:gap-2xl lg:gap-5xl',
        className,
      )}
      {...(attr && { 'data-sanity': attr.toString() })}
    >
      {callout.showImage && imageBaseUrl && (
        <div
          className={cn(
            'aspect-[52/33] lg:aspect-[310/207] overflow-hidden w-full flex-shrink-0',
            isArticleCallout &&
              '!aspect-auto p-xl pb-0 md:pb-xl lg:p-5xl rounded-md shrink-0 md:w-[200px] md:pr-0 lg:w-[310px] lg:pr-0',
          )}
        >
          <Image
            loader={({ width }) => {
              return urlForImage(callout.image)?.width(width).url() || imageBaseUrl
            }}
            src={imageBaseUrl}
            alt={callout.heading ? toPlainText(callout.heading) : ''}
            width={310}
            height={310}
            className={cn('h-full w-full object-cover', isArticleCallout && 'rounded-md')}
            sizes="(max-width: 768px) 100vw, 310px"
            loading="lazy"
          />
        </div>
      )}

      <div
        className={cn(
          'flex flex-col gap-md lg:gap-lg p-xl lg:p-4xl my-0 mx-auto w-full flex-grow',
          isArticleCallout && 'md:pl-0 lg:p-5xl lg:pl-0',
        )}
      >
        {callout.showIcon && callout.icon && (
          <div className="lg:[&_svg]:!h-[24px] lg:[&_svg]:!w-[24px]">
            <Icon icon={callout.icon as any} size={20} />
          </div>
        )}

        {callout.overline && (
          <div className="size-overline-sm pb-sm -mb-md lg:-mb-lg lg:size-overline-md text-primary-700 font-black uppercase tracking-widest">
            {callout.overline}
          </div>
        )}

        {callout.heading && (
          <PortableText
            value={callout.heading as PortableTextBlock[]}
            tracking={tracking}
            paragraphClassName={cn(
              'font-bold text-neutral-900 !mb-0 line-clamp-3 pb-[3px]',
              headingSizeClasses[headingSize as keyof typeof headingSizeClasses],
            )}
          />
        )}

        {callout.text && (
          <PortableText
            value={callout.text as PortableTextBlock[]}
            tracking={tracking}
            paragraphClassName={cn('size-body-sm lg:size-body-lg', textClassName)}
          />
        )}

        {callout.showButton && callout.button && (
          <div className="mt-auto">
            <Button
              button={callout.button as any}
              tracking={tracking}
              className={cn(buttonSizeClasses({ lg: 'md' }), buttonAlignmentClasses[alignment])}
            />
          </div>
        )}
      </div>
    </div>
  )
}
